import { useState, useEffect, createContext, useContext } from 'react';
import { X, Download, Smartphone, Monitor, CheckCircle } from 'lucide-react';

// PWA Context for sharing install state across components
const PWAContext = createContext(null);

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export const PWAProvider = ({ children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  // Cookie helpers
  const setCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  };

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : null;
  };

  const deleteCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  useEffect(() => {
    // Check if already installed
    const checkInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        setCookie('pwa_installed', 'true', 365);
        return true;
      }
      if (window.navigator.standalone === true) {
        setIsInstalled(true);
        setCookie('pwa_installed', 'true', 365);
        return true;
      }
      // Check cookie for installed state
      if (getCookie('pwa_installed') === 'true') {
        // Verify it's still installed
        if (!window.matchMedia('(display-mode: standalone)').matches) {
          // App was uninstalled
          deleteCookie('pwa_installed');
          deleteCookie('pwa_dismissed');
          return false;
        }
        setIsInstalled(true);
        return true;
      }
      return false;
    };

    if (checkInstalled()) return;

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user dismissed popup recently (7 days)
      const dismissed = getCookie('pwa_dismissed');
      if (dismissed) return;

      // Show popup after 3 seconds
      setTimeout(() => {
        if (!getCookie('pwa_installed') && !getCookie('pwa_dismissed')) {
          setShowPopup(true);
        }
      }, 3000);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPopup(false);
      setDeferredPrompt(null);
      setCookie('pwa_installed', 'true', 365);
      deleteCookie('pwa_dismissed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check display mode changes (for uninstall detection)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (e) => {
      if (!e.matches && getCookie('pwa_installed') === 'true') {
        // App was uninstalled
        deleteCookie('pwa_installed');
        deleteCookie('pwa_dismissed');
        setIsInstalled(false);
      }
    };
    mediaQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      mediaQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const installApp = async () => {
    if (!deferredPrompt) return false;

    setIsInstalling(true);
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setCookie('pwa_installed', 'true', 365);
        deleteCookie('pwa_dismissed');
      }
      
      setDeferredPrompt(null);
      setShowPopup(false);
      return outcome === 'accepted';
    } catch (error) {
      console.error('Install error:', error);
      return false;
    } finally {
      setIsInstalling(false);
    }
  };

  const dismissPopup = () => {
    setShowPopup(false);
    setCookie('pwa_dismissed', 'true', 7); // Don't show for 7 days
  };

  const canInstall = !!deferredPrompt && !isInstalled;

  return (
    <PWAContext.Provider value={{ 
      canInstall, 
      isInstalled, 
      installApp, 
      showPopup, 
      dismissPopup,
      isInstalling 
    }}>
      {children}
    </PWAContext.Provider>
  );
};

// Install Popup Component
export const PWAInstallPopup = () => {
  const { showPopup, dismissPopup, installApp, isInstalling } = usePWA();

  if (!showPopup) return null;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-scaleIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#014DB7] to-[#0066FF] p-6 text-white relative">
          <button 
            onClick={dismissPopup}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              {isMobile ? (
                <Smartphone className="w-8 h-8" />
              ) : (
                <Monitor className="w-8 h-8" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">Install DME R'US</h2>
              <p className="text-white/80 text-sm">Get the full app experience</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            Install our app on your {isMobile ? 'device' : 'desktop'} for quick access, 
            offline support, and a native app experience.
          </p>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <CheckCircle className="w-5 h-5 text-[#84CC16] flex-shrink-0" />
              <span>Quick access from your {isMobile ? 'home screen' : 'desktop'}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <CheckCircle className="w-5 h-5 text-[#84CC16] flex-shrink-0" />
              <span>Works offline with cached data</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <CheckCircle className="w-5 h-5 text-[#84CC16] flex-shrink-0" />
              <span>Faster loading and smoother experience</span>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={dismissPopup}
              className="flex-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors"
            >
              Not Now
            </button>
            <button
              onClick={installApp}
              disabled={isInstalling}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-[#014DB7] to-[#0066FF] text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {isInstalling ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Installing...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Install App
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

// Footer Install Button Component
export const PWAFooterButton = () => {
  const { canInstall, isInstalled, installApp, isInstalling } = usePWA();

  if (isInstalled) {
    return (
      <span className="text-[#84CC16] flex items-center gap-2">
        <CheckCircle className="w-4 h-4" />
        App Installed ✓
      </span>
    );
  }

  if (!canInstall) {
    return null;
  }

  return (
    <button
      onClick={installApp}
      disabled={isInstalling}
      className="text-gray-400 hover:text-[#84CC16] transition-colors flex items-center gap-2"
    >
      <Download className="w-4 h-4" />
      {isInstalling ? 'Installing...' : 'Install Desktop App'}
    </button>
  );
};

export default PWAProvider;
